import { Form } from 'formik';
import { FC } from 'react';
import * as Yup from 'yup';
import { Modal as QcsModal } from '@mui/material';

import { QcsModalBox } from '../../../common/basic/QcsModalBox';
import { FormikDebounce } from '../../../common/form/FormikDebounce';
import { FormContainer } from '../../../common/form/FormContainer';
import { SubmitButton } from '../../../common/form/SubmitButton';
import { SectionHeader } from '../../../common/SectionHeader';
import { validations } from '../../../../utils/validations';
import { useAppSelector } from '../../../../store';
import { selectCompanyCustomization } from '../../../../store/entities/companyCustomization';
import { VisitorPersonFields } from '../../VisitorPersonFields';
import { prefillPersonFromEmail } from '../../prefillPersonFromEmail';
import { VisitationAddModalOneTimeConflict, VisitationAddOneTimeConflictData } from '../../../visitation/VisitationAddModalOneTimeConflict';
import { OneTimePersonDto } from '@api/models/invitation/OneTimePersonDto';
import { invitationPersonService } from '@api/services/services';
import { useInvitation } from './invitationFunctions';
import { useConflictResolver } from '../../useConflictResolver';
import { resolveVisitorConflicts } from '../../resolveVisitorConflicts';
import { toOneTimePersonDto } from '../../oneTimePerson';

interface OneTimePersonForm {
  name: string;
  lastName: string;
  language: string;
  phone?: string;
  email?: string;
}

interface Props {
  open: boolean;
  onClose: (created?: boolean) => void;
}

export const InvTabWorkersAddOneTimeModal: FC<Props> = ({ open, onClose }) => {
  const { t, invitation, callWithLock } = useInvitation();
  const companyCustomization = useAppSelector(selectCompanyCustomization);
  const visitorConflictResolver = useConflictResolver<VisitationAddOneTimeConflictData<OneTimePersonDto>, OneTimePersonDto>();

  const handleSubmit = async (data: OneTimePersonForm) => {
    const dto = toOneTimePersonDto(data);
    const result = await resolveVisitorConflicts([dto], visitorConflictResolver.request);
    if (!result) return;
    const finalDto = result.persons[0];

    await callWithLock(async () => {
      await invitationPersonService.addOneTimePerson(invitation.id!, finalDto);
      onClose(true);
    });
  };

  return (
    <>
      <VisitationAddModalOneTimeConflict data={visitorConflictResolver.conflict} onClose={visitorConflictResolver.close} />

      <QcsModal open={open} onClose={() => onClose()}>
        <QcsModalBox>
          <SectionHeader title="invitation.persons.addVisit" />
          <FormikDebounce<OneTimePersonForm>
            initialValues={{
              name: '',
              lastName: '',
              language: companyCustomization?.defaultLanguage ?? '',
              phone: '',
              email: '',
            }}
            validationSchema={Yup.object({
              name: validations.stringRequired(t),
              lastName: validations.stringRequired(t),
              language: validations.stringRequired(t),
              phone: validations.phoneOptional(t),
              email: validations.emailOptional(t),
            })}
            onSubmit={handleSubmit}
          >
            {({ setFieldValue }) => (
              <Form>
                <FormContainer>
                  <VisitorPersonFields
                    allowedLanguages={companyCustomization?.applicationLanguages}
                    onEmailBlur={async (email) => {
                      await prefillPersonFromEmail(email, setFieldValue);
                    }}
                  />
                  <SubmitButton />
                </FormContainer>
              </Form>
            )}
          </FormikDebounce>
        </QcsModalBox>
      </QcsModal>
    </>
  );
};
